import React, { useState } from "react";
import {
  Stack,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  Divider,
} from "@mui/material";
import { LocationOn, CreditCard, Close } from "@mui/icons-material";
import { dateFormatter } from "../../utils/masking";

interface DeliveryAddress {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  favorite?: boolean;
  [key: string]: any;
}

interface GiftCard {
  accountId?: string;
  balance?: number;
  cardNumber?: string;
  cardSuffix?: string;
  createdAt?: string;
  default?: boolean;
  updatedAt?: string;
}

interface Customer {
  giftCards?: GiftCard[];
  giftCardList?: GiftCard[];
  userDeliveryAddressList?: DeliveryAddress[];
}

interface AdditionalInfoProps {
  customer: Customer;
}

export const AdditionalInfo = ({ customer }: AdditionalInfoProps) => {
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [giftCardDialogOpen, setGiftCardDialogOpen] = useState(false);

  const userDeliveryAddressList: DeliveryAddress[] = customer?.userDeliveryAddressList || [];
  // Support both property names: giftCards and giftCardsList
  const giftCardsList: GiftCard[] = customer?.giftCards || customer?.giftCardList || [];

  // Get primary address: favorite one, or the single address if only one exists
  const getPrimaryAddress = (): DeliveryAddress | null => {
    if (userDeliveryAddressList.length === 0) {return null;}
    if (userDeliveryAddressList.length === 1) {return userDeliveryAddressList[0];}
    return userDeliveryAddressList.find((addr) => addr.favorite) || userDeliveryAddressList[0];
  };

  const primaryAddress = getPrimaryAddress();

  const formatAddress = (address: DeliveryAddress | null): string => {
    if (!address) {return "No Address";}
    const parts = [
      address.addressLine1,
      address.addressLine2,
      address.city,
      address.state,
      address.postalCode,
    ].filter(Boolean);
    return parts.join(", ") || "No Address";
  };

  return (
    <>
      <Stack spacing={1}>
        {/* Gift Cards */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" fontWeight={600}>
            Gift Cards:
          </Typography>
          {giftCardsList.length > 0 ? (
            <Button
              size="small"
              startIcon={<CreditCard sx={{ fontSize: 16 }} />}
              onClick={() => setGiftCardDialogOpen(true)}
              sx={{ textTransform: "none", fontSize: "0.8rem", p: 0 }}
            >
              {giftCardsList.length} card(s)
            </Button>
          ) : (
            <Typography variant="body2">No Gift Cards</Typography>
          )}
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="body2" fontWeight={600}>
              Primary Address:
            </Typography>
            {primaryAddress?.favorite && (
              <Chip label="Favorite" size="small" color="primary" sx={{ mt: 0.5, height: 18, fontSize: "0.6rem" }} />
            )}
          </Box>
          <Box sx={{ textAlign: "right", maxWidth: "60%" }}>
            <Typography variant="body2" fontSize="0.75rem">
              {formatAddress(primaryAddress)}
            </Typography>
            {userDeliveryAddressList.length > 1 && (
              <Button
                size="small"
                startIcon={<LocationOn sx={{ fontSize: 14 }} />}
                onClick={() => setAddressDialogOpen(true)}
                sx={{ mt: 0.5, textTransform: "none", fontSize: "0.7rem", p: 0 }}
              >
                View All ({userDeliveryAddressList.length})
              </Button>
            )}
          </Box>
        </Stack>
      </Stack>

      {/* Delivery Addresses Dialog */}
      <Dialog
        open={addressDialogOpen}
        onClose={() => setAddressDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>All Delivery Addresses</DialogTitle>
        <DialogContent>
          <List>
            {userDeliveryAddressList.map((address) => (
              <ListItem
                key={`${address.addressLine1 ?? ""}-${address.city ?? ""}-${address.postalCode ?? ""}-${String(address.favorite)}`}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  mb: 1,
                  backgroundColor: address.favorite ? "#e3f2fd" : "transparent",
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body1">
                        {address.addressLine1 || "No street address"}
                      </Typography>
                      {address.favorite && (
                        <Chip label="Favorite" size="small" color="primary" sx={{ height: 20, fontSize: "0.7rem" }} />
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography variant="body2" color="text.secondary">
                      {[address.addressLine2, address.city, address.state, address.postalCode]
                        .filter(Boolean)
                        .join(", ") || "No additional details"}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddressDialogOpen(false)}
            variant="outlined"
            sx={{
              color: "#041E42",
              borderColor: "#041E42",
              "&:hover": {
                borderColor: "#0a3d7a",
                backgroundColor: "rgba(4, 30, 66, 0.04)",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Gift Cards Dialog */}
      <Dialog
        open={giftCardDialogOpen}
        onClose={() => setGiftCardDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Gift Cards ({giftCardsList.length})
          <IconButton onClick={() => setGiftCardDialogOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <List sx={{ p: 0 }}>
            {giftCardsList.map((card, index) => (
              <React.Fragment key={card.accountId || index}>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          •••• {card.cardSuffix || "N/A"}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: "bold", color: "#2e7d32" }}>
                          ${(card.balance || 0).toFixed(2)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Card Number: {card.cardNumber || "N/A"}
                        </Typography>
                        {card.accountId && (
                          <Typography variant="body2" color="text.secondary">
                            Account ID: {card.accountId}
                          </Typography>
                        )}
                        {card.createdAt && (
                          <Typography variant="body2" color="text.secondary">
                            Created: {dateFormatter(card.createdAt) ?? "N/A"}
                          </Typography>
                        )}
                        {card.default && (
                          <Chip label="Default" size="small" color="primary" sx={{ mt: 0.5 }} />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < giftCardsList.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
};
